import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import SignalCellular1BarIcon from '@mui/icons-material/SignalCellular1Bar';
import SignalCellular2BarIcon from '@mui/icons-material/SignalCellular2Bar';
import SignalCellular3BarIcon from '@mui/icons-material/SignalCellular3Bar';
import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar';
import SignalCellularOffIcon from '@mui/icons-material/SignalCellularOff';

// The Network Information API (navigator.connection) is Chromium-only (no Firefox/Safari
// support) — when it's absent we can still detect online/offline via navigator.onLine and the
// window online/offline events, we just can't distinguish signal quality, so we show full bars
// for "online, quality unknown" rather than guessing.
type EffectiveType = '4g' | '3g' | '2g' | 'slow-2g' | undefined;

interface NetworkInformation extends EventTarget {
  effectiveType?: EffectiveType;
}

function getConnection(): NetworkInformation | undefined {
  return (navigator as Navigator & { connection?: NetworkInformation }).connection;
}

export default function NetworkStatusIndicator() {
  const { t } = useTranslation('nav');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [effectiveType, setEffectiveType] = useState<EffectiveType>(() => getConnection()?.effectiveType);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const connection = getConnection();
    const handleChange = () => setEffectiveType(connection?.effectiveType);
    connection?.addEventListener('change', handleChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      connection?.removeEventListener('change', handleChange);
    };
  }, []);

  if (!isOnline) {
    return (
      <Tooltip title={t('networkOffline')}>
        <SignalCellularOffIcon fontSize="small" color="error" />
      </Tooltip>
    );
  }

  const { Icon, label } = (
    {
      'slow-2g': { Icon: SignalCellular1BarIcon, label: t('networkPoor') },
      '2g': { Icon: SignalCellular2BarIcon, label: t('networkFair') },
      '3g': { Icon: SignalCellular3BarIcon, label: t('networkGood') },
      '4g': { Icon: SignalCellular4BarIcon, label: t('networkExcellent') },
    } as const
  )[effectiveType ?? '4g'] ?? { Icon: SignalCellular4BarIcon, label: t('networkOnline') };

  return (
    <Tooltip title={label}>
      <Icon fontSize="small" color="action" />
    </Tooltip>
  );
}
